import React, { FC, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { MESSAGE_TYPES, IGlobalMessage, IGlobalMessageCta } from 'types/globalMessaging'
import { showMessage, hideMessage } from 'utils/actions/globalMessaging'
import { globalMessagingSelector } from 'utils/selectors/globalMessaging'
import { Toast, TToastNewState, ToastVariation } from './Toast'

const toastVariationToGlobalToastType: { [toastType in ToastVariation]: MESSAGE_TYPES } = {
  alert: MESSAGE_TYPES.alert,
  default: MESSAGE_TYPES.info,
}

// Global Toast can only render a string message for now.
interface TGlobalToastNewState extends Omit<TToastNewState, 'toastMessage'> {
  toastMessage: string
  /**
   * The number of milliseconds to wait before automatically calling
   * the closing the toast. This behavior is disabled by default with
   * the `null` value.
   *
   * @default 5000
   */
  toastDuration?: number | null
  toastCta?: IGlobalMessageCta | undefined
}

export const useGlobalToast = () => {
  const dispatch = useDispatch()

  const openToast = useCallback(
    (newToastState: TGlobalToastNewState) => {
      const { toastCta, toastDuration, toastMessage, toastType = 'default' } = newToastState
      dispatch(
        showMessage(toastMessage, {
          type: toastVariationToGlobalToastType[toastType],
          closeDelay: toastDuration,
          cta: toastCta,
        })
      )
    },
    [dispatch]
  )

  return { openToast }
}

const globalMessagingTypeToToastVariation = (globalMessagingType?: MESSAGE_TYPES) => {
  switch (globalMessagingType) {
    case MESSAGE_TYPES.alert:
      return 'alert'
    default:
      return 'default'
  }
}

interface IRenderedMessageProps {
  message: IGlobalMessage | undefined
}

/**
 * Handles some conditions around rendering a Toast message
 * with url/label for Toasts with CTAs
 */
export const RenderedMessage: FC<IRenderedMessageProps> = ({ message: messageProp }) => {
  const { cta, message } = messageProp || {}
  const { label, target, url } = cta || {}

  return (
    <>
      {message}
      {url && label ? (
        <>
          {' '}
          <a href={url} target={target}>
            {label}
          </a>
        </>
      ) : (
        ''
      )}
    </>
  )
}

/**
 * A redux-connected Toast component that utilizes the existing globalMessaging
 * state in our redux store.
 *
 * Render this component at the top level of any app to display a toast
 * notification by dispatching the showMessage action from anywhere in the
 * app.
 */
export const GlobalToast = () => {
  const dispatch = useDispatch()

  const { message } = useSelector(globalMessagingSelector)

  const closeToastHandler = () => {
    dispatch(hideMessage(message?.id ?? ''))
  }

  return (
    <Toast
      variation={globalMessagingTypeToToastVariation(message?.type)}
      message={<RenderedMessage message={message} />}
      open={!!message}
      onClose={closeToastHandler}
      duration={message?.closeDelay}
    />
  )
}
